//NOTE: 에어브릿지 이벤트 발생 시 Pixel이벤트, GA4태그를 함께 트래킹 @ahn 2025.07.09
import airbridge from 'airbridge-web-sdk-loader'
import { EventOption } from 'airbridge-web-sdk-loader/build/airbridge_api'
import ReactPixel from 'react-facebook-pixel'

import type { components } from '../constants/schema'

export type AbrUser = {
  id?: components['schemas']['UserDto']['id']
  email?: components['schemas']['UserDto']['email']
}

export type AbrUserAuthOauthDto = {
  provider?: components['schemas']['UserAuthOauthDto']['provider']
}

export type AbrProduct = {
  id?: components['schemas']['ProductDto']['id']
  name?: components['schemas']['ProductDetailDto']['name']
  overallStatusLabel?: components['schemas']['ProductDetailDto']['overallStatusLabel']
}

export type AbrArticle = {
  id?: components['schemas']['ArticleDto']['id']
}

export type AbrArticleCategory = {
  name?: components['schemas']['ArticleCategoryDto']['name']
}

export type AbrEvent = {
  id?: components['schemas']['EventDto']['id']
  title?: components['schemas']['EventDto']['title']
}

export type AbrWithdrawalAccount = {
  bankName?: components['schemas']['WithdrawalAccountDto']['bankName']
}

export type AbrPurchase = {
  count?: components['schemas']['PurchaseDto']['count']
  amount?: components['schemas']['PurchaseDto']['amount']
  orderSequence?: components['schemas']['PurchaseDto']['orderSequence']
}

export type AbrUserProduct = {
  id?: components['schemas']['UserProductDto']['id']
  subscriptionShares?: components['schemas']['UserProductDto']['subscriptionShares']
  balance?: components['schemas']['UserProductDto']['balance']
}

export type AbrUserTermsMarketing = {
  hasConsented?: components['schemas']['ResBooleanResult']['result']
}

/** Airbridge: 유저 정보 등록 */
export const abrSetUser = (user: AbrUser) => {
  airbridge.setUserID(`${user?.id ?? ''}`)
  airbridge.setUserEmail(`${user?.email ?? ''}`)
}

/** Airbridge: 회원가입을 완료한 시점 */
export const abrUserSignup = (auth: AbrUserAuthOauthDto) => {
  const option: EventOption = {
    action: auth?.provider ?? '',
  }

  airbridge.events.send('airbridge.user.signup', option)
  customGtagEvent('sign_up', { method: auth?.provider ?? '' })
  ReactPixel.track('CompleteRegistration')
}

/** Airbridge: 회원가입을 완료하여 약관동의 페이지가 뜬 시점 */
export const abrViewTerms = () => {
  airbridge.events.send('view_terms')
  customGtagEvent('view_terms')
}

/** Airbridge: 간편 로그인을 성공하여 홈 화면으로 자동 이동된 시점 */
export const abrUserSignin = (auth: AbrUserAuthOauthDto) => {
  const option: EventOption = {
    action: auth?.provider ?? '',
  }

  airbridge.events.send('airbridge.user.signin', option)
  customGtagEvent('login', { method: auth?.provider ?? '' })
}

/** Airbridge: 로그아웃한 시점 */
export const abrUserSignout = () => {
  airbridge.events.send('airbridge.user.signout')
  customGtagEvent('sign_out')
  airbridge.clearUser()
}

/** Airbridge: 청약 탭을 클릭하여 이동된 시점 */
export const abrViewSubscriptionProductList = () => {
  airbridge.events.send('view_subscription_products_list')
  customGtagEvent('view_subscription_products_list')
}

/** Airbridge: 청약 작품 클릭하여 해당 상세페이지로 이동한 시점 */
export const abrViewSubscriptionProductDetails = (product: AbrProduct) => {
  const option: EventOption = {
    action: product?.overallStatusLabel ?? '',
    label: product?.name ?? '',
  }

  airbridge.events.send('view_subscription_product_details', option)
  customGtagEvent('view_item', {
    items: [
      {
        item_id: `${product?.id ?? ''}`,
        item_name: product?.name ?? '',
        item_category: product?.overallStatusLabel ?? '',
      },
    ],
  })
}

/** Airbridge: 콘텐츠 탭을 클릭하여 이동된 시점 */
export const abrViewContentsList = (category: AbrArticleCategory) => {
  const option: EventOption = {
    action: category?.name ?? '',
  }

  airbridge.events.send('view_contents_list', option)
  customGtagEvent('view_contents_list', { items: [{ item_category: category?.name ?? '' }] })
}

/** Airbridge: 콘텐츠 화면에서 특정 콘텐츠를 클릭하여 해당 페이지로 이동된 시점 */
export const abrViewContentDetails = (article: AbrArticle, category: AbrArticleCategory) => {
  const option: EventOption = {
    action: category?.name ?? '',
    label: `${article?.id ?? ''}`,
  }

  airbridge.events.send('view_content_details', option)
  customGtagEvent('view_content_details', {
    items: [{ item_id: article?.id ?? '', item_category: category?.name ?? '' }],
  })
  ReactPixel.track('ViewContent')
}

/** Airbridge: 이벤트 상세페이지를 진입한 시점 */
export const abrViewPromotionPage = (event: AbrEvent) => {
  const option: EventOption = {
    action: event?.title ?? '',
    label: `${event?.id ?? ''}`,
  }

  airbridge.events.send('view_promotion_page', option)
  customGtagEvent('select_promotion', { promotion_id: `${event?.id ?? ''}`, promotion_name: event?.title ?? '' })
}

/** Airbridge: 알림설정 > 마케팅 정보 수신동의 버튼 토글 클릭한 시점 */
export const abrClickButtonMarketing = (res: AbrUserTermsMarketing) => {
  let hasConsented = '' //동의여부
  if (res.hasConsented === true) hasConsented = 'ON'
  if (res.hasConsented === false) hasConsented = 'OFF'

  const option: EventOption = {
    action: hasConsented,
  }

  airbridge.events.send('click_button_marketing', option)
  if (res.hasConsented === true) {
    customGtagEvent('click_button_marketing')
  }
  ReactPixel.track('Subscribe')
}

/** Airbridge: 유저가 PASS 본인인증을 완료한 시점 */
export const abrCompleteIdentification = () => {
  airbridge.events.send('complete_identification')
  customGtagEvent('complete_identification')
  ReactPixel.track('TutorialCompletion')
}

/** Airbridge: 유저가 자택주소 입력을 완료한 시점 */
export const abrCompleteAddressInput = () => {
  airbridge.events.send('complete_address_input')
  customGtagEvent('complete_address_input')
}

/** Airbridge: 유저가 투자적합성 테스트를 완료한 시점 */
export const abrCompleteSuitabilityTest = () => {
  airbridge.events.send('complete_suitability_test')
  customGtagEvent('complete_suitability_test')
}

/** Airbridge: 유저가 출금계좌 등록 페이지에 진입한 시점 */
export const abrViewWithdrawalAccount = () => {
  airbridge.events.send('view_withdrawal_account')
  customGtagEvent('view_withdrawal_account')
}

/** Airbridge: 유저가 출금계좌 등록 페이지에서 시작하기 버튼 클릭 후 다음 페이지에 로딩된 시점 */
export const abrStartWithdrawalAccount = () => {
  airbridge.events.send('start_withdrawal_account_register')
  customGtagEvent('start_withdrawal_account_register')
}

/** Airbridge: '출금 계좌 등록을 완료했어요' 알럿이 뜨는 시점 */
export const abrCompleteWithdrawalAccountRegister = (withdrawalAccount: AbrWithdrawalAccount) => {
  const option: EventOption = {
    action: withdrawalAccount?.bankName ?? '',
  }

  airbridge.events.send('complete_withdrawal_account_register', option)
  customGtagEvent('complete_withdrawal_account_register')
}

/** Airbridge: '예치금 계좌 생성하기' 페이지에 진입한 시점 */
export const abrStartDepositAccountRegister = () => {
  airbridge.events.send('start_deposit_account_register')
  customGtagEvent('start_deposit_account_register')
}

/** Airbridge: '예치금 충전 계좌 발급이 완료 페이지에 진입한 시점 */
export const abrCompleteDepositAccountRegister = () => {
  airbridge.events.send('complete_deposit_account_register')
  customGtagEvent('complete_deposit_account_register')
  ReactPixel.track('AddPaymentInfo')
}

/** Airbridge: '청약 신청하기' 버튼을 클릭하여 페이지가 로딩된 시점 */
export const abrStartSubscriptionProduct = (product: AbrProduct) => {
  const option: EventOption = {
    action: product.name,
  }

  airbridge.events.send('start_subscription_product', option)
  customGtagEvent('begin_checkout', {
    items: [
      {
        item_id: `${product?.id ?? ''}`,
        item_name: product?.name ?? '',
        item_category: product?.overallStatusLabel ?? '',
      },
    ],
  })
  ReactPixel.track('InitiateCheckout')
}

/** Airbridge: '청약 신청하기' 버튼을 클릭하고 매각 위탁에 대한 동의/투자계약서를 동의한 시점 */
export const abrSuccessSubscriptionProduct = (product?: AbrProduct, userProduct?: AbrUserProduct) => {
  const productName = `${product?.name ?? ''}` //action
  const count = `${userProduct?.subscriptionShares ?? ''}` //label
  const balance = userProduct?.balance ?? 0 //value
  const product_id = `${product?.id ?? ''}` //semanticAttributes.product_id
  const transaction_id = userProduct?.id ? String(userProduct?.id).padStart(8, '0') : '' //semanticAttributes.transaction_id

  const option: EventOption = {
    action: productName,
    label: count,
    value: balance,
    semanticAttributes: {
      product_id,
      transaction_id,
      currency: 'KRW',
    },
  }

  airbridge.events.send('success_subscription_product', option)
  customGtagEvent('purchase', {
    transaction_id: transaction_id,
    value: balance,
    currency: 'KRW',
    items: [
      {
        item_id: `${product?.id ?? ''}`,
        item_name: product?.name ?? '',
        item_category: product?.overallStatusLabel ?? '',
        quantity: userProduct?.subscriptionShares ?? 0,
      },
    ],
  })
  ReactPixel.track('Purchase', { currency: 'KRW', value: balance })
}

/** Airbridge: 자산상세 페이지에서 청약취소 버튼 클릭하여 '네' 버튼을 클릭한 시점 */
export const abrCancelSubscriptionProduct = (product?: AbrProduct, userProduct?: AbrUserProduct) => {
  const productName = `${product?.name ?? ''}` //action
  const count = `${userProduct?.subscriptionShares ?? ''}` //label
  const balance = userProduct?.balance ?? 0 //value
  const product_id = `${product?.id ?? ''}` //semanticAttributes.product_id
  const transaction_id = userProduct?.id ? String(userProduct?.id).padStart(8, '0') : '' //semanticAttributes.transaction_id

  const option: EventOption = {
    action: productName,
    label: count,
    value: balance,
    semanticAttributes: {
      product_id,
      transaction_id,
      currency: 'KRW',
    },
  }

  airbridge.events.send('cancel_subscription_product', option)
  customGtagEvent('refund', {
    transaction_id: transaction_id,
    value: balance,
    currency: 'KRW',
    items: [
      {
        item_id: `${product?.id ?? ''}`,
        item_name: product?.name ?? '',
        item_category: product?.overallStatusLabel ?? '',
        quantity: userProduct?.subscriptionShares ?? 0,
      },
    ],
  })
}

function customGtagEvent(eventName: string, params?: any) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    ;(window as any).gtag('event', eventName, params || {})
  }
}
